import { addressComparisonKey } from "@/utils/address";

/**
 * Mutable tracker for queued withdrawal state. Lives in a `useRef` — mutations
 * don't trigger re-renders. Encapsulates the dedup, version-gating, partial-fill
 * accumulation, stuck-detection, and API-failure tracking that the WS and poll
 * paths in `useTransferConfirmation` both need.
 *
 * All keys come from [queuedTransferKey], so the WS and poll paths agree on
 * them even when their address spellings differ in zero-padding.
 */
export interface QueuedTracker {
  // ── Dedup ──────────────────────────────────────────────────────────────────
  isAdvanced(key: string): boolean;
  advance(key: string): void;

  // ── Version gating ─────────────────────────────────────────────────────────
  shouldSkipVersion(key: string, version: number): boolean;
  setLastSeenVersion(key: string, version: number): void;

  // ── Partial-fill accumulation ──────────────────────────────────────────────
  getWsAccumulated(key: string): number | undefined;
  setWsAccumulated(key: string, value: number): void;

  // ── Stuck detection ────────────────────────────────────────────────────────
  incrementPollCycles(key: string): number;

  // ── API failure tracking (keyed by address, not composite key) ─────────────
  incrementApiFailures(addr: string): number;
  resetApiFailures(addr: string): void;

  // ── Cleanup ────────────────────────────────────────────────────────────────
  clearForAddresses(removed: Set<string>): void;
}

export function queuedTransferKey(userAddress: string, requestId: string): string {
  return `${addressComparisonKey(userAddress)}:${requestId}`;
}

export function createQueuedTracker(): QueuedTracker {
  const advanced = new Set<string>();
  const lastSeenVersion = new Map<string, number>();
  const pollCycles = new Map<string, number>();
  const wsAccumulated = new Map<string, number>();
  const apiFailures = new Map<string, number>();

  return {
    isAdvanced: (key) => advanced.has(key),

    advance(key) {
      advanced.add(key);
      pollCycles.delete(key);
      wsAccumulated.delete(key);
      lastSeenVersion.delete(key);
    },

    shouldSkipVersion(key, version) {
      const last = lastSeenVersion.get(key) ?? -1;
      return version <= last;
    },

    setLastSeenVersion: (key, version) => lastSeenVersion.set(key, version),

    getWsAccumulated: (key) => wsAccumulated.get(key),
    setWsAccumulated: (key, value) => wsAccumulated.set(key, value),

    incrementPollCycles(key) {
      const next = (pollCycles.get(key) ?? 0) + 1;
      pollCycles.set(key, next);
      return next;
    },

    incrementApiFailures(addr) {
      const next = (apiFailures.get(addr) ?? 0) + 1;
      apiFailures.set(addr, next);
      return next;
    },

    resetApiFailures: (addr) => apiFailures.set(addr, 0),

    clearForAddresses(removed) {
      const deleteMatching = (keys: Iterable<string>, del: (k: string) => void) => {
        for (const key of keys) {
          const addrKey = key.split(":")[0];
          if (addrKey && removed.has(addrKey)) del(key);
        }
      };
      deleteMatching(advanced, (k) => advanced.delete(k));
      deleteMatching(lastSeenVersion.keys(), (k) => lastSeenVersion.delete(k));
      deleteMatching(wsAccumulated.keys(), (k) => wsAccumulated.delete(k));
      deleteMatching(pollCycles.keys(), (k) => pollCycles.delete(k));
    },
  };
}
